/**
 * YoYChart
 *
 * Grouped bar chart comparing Before vs After billing data.
 * When a single professional is selected, renders one full-width chart.
 * When viewing all professionals, facets into a grid of mini-charts
 * with optional go-live date subtitles.
 *
 * Uses recharts BarChart with grouped (non-stacked) bars.
 */

import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { YoYDataPoint, YoYGranularity } from "../../types/dashboard";
import { YOY_CHART_COLORS, YOY_CHART_LAYOUT } from "../../config/dashboardTheme";
import { formatGoLiveDate } from "../../utils/adminHelpers";

interface YoYChartProps {
  data: YoYDataPoint[];
  granularity: YoYGranularity;
  selectedProfId: string | null;
  goLiveDates?: Record<string, string>;
}

interface ChartDataPoint {
  label: string;
  Before: number;
  After: number;
}

function buildChartData(
  data: YoYDataPoint[],
  granularity: YoYGranularity
): ChartDataPoint[] {
  // Group by period_num, aggregate hours across both periods
  const map = new Map<number, { Before: number; After: number }>();

  for (const d of data) {
    const existing = map.get(d.period_num) ?? { Before: 0, After: 0 };
    existing[d.period] += d.hours;
    map.set(d.period_num, existing);
  }

  // Sort by period_num and create labels
  return Array.from(map.entries())
    .sort(([a], [b]) => a - b)
    .map(([num, values]) => ({
      label: granularity === "weekly" ? `W${num}` : `M${num}`,
      Before: Math.round(values.Before * 10) / 10,
      After: Math.round(values.After * 10) / 10,
    }));
}

function SingleChart({
  chartData,
  title,
  subtitle,
  height = 350,
}: {
  chartData: ChartDataPoint[];
  title?: string;
  subtitle?: string;
  height?: number;
}) {
  return (
    <div>
      {title && (
        <div className="mb-2">
          <h4 className="text-sm font-medium text-foreground">{title}</h4>
          {subtitle && (
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          )}
        </div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={chartData} barGap={2}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 12 }}
            stroke="#6b7280"
          />
          <YAxis tick={{ fontSize: 12 }} stroke="#6b7280" />
          <Tooltip
            contentStyle={{
              backgroundColor: "var(--popover)",
              border: "1px solid var(--border)",
              borderRadius: "8px",
              fontSize: "13px",
            }}
            formatter={(value) => [`${Number(value)} hrs`, undefined]}
          />
          <Legend />
          <Bar
            dataKey="Before"
            fill={YOY_CHART_COLORS.before}
            radius={[4, 4, 0, 0]}
            maxBarSize={60}
          />
          <Bar
            dataKey="After"
            fill={YOY_CHART_COLORS.after}
            radius={[4, 4, 0, 0]}
            maxBarSize={60}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function YoYChart({
  data,
  granularity,
  selectedProfId,
  goLiveDates,
}: YoYChartProps) {
  // All professionals: faceted grid sorted by go-live date (earliest first)
  // NOTE: useMemo must be called before any conditional return (Rules of Hooks)
  const profGroups = useMemo(() => {
    // Key by prof_id (not prof_name) to avoid merging data for attorneys
    // who share the same full name.
    const groups = new Map<string, { profName: string; points: YoYDataPoint[] }>();
    for (const d of data) {
      const existing = groups.get(d.prof_id) ?? { profName: d.prof_name, points: [] };
      existing.points.push(d);
      groups.set(d.prof_id, existing);
    }
    // Sort by Hourglass go-live date ascending — earliest adopters first.
    // Professionals without a go-live date sort to the end.
    return Array.from(groups.entries()).sort(([idA], [idB]) => {
      const dateA = goLiveDates?.[idA] ?? "9999-12-31";
      const dateB = goLiveDates?.[idB] ?? "9999-12-31";
      return dateA.localeCompare(dateB);
    });
  }, [data, goLiveDates]);

  // Single professional: one full chart
  if (selectedProfId) {
    const chartData = buildChartData(data, granularity);
    return <SingleChart chartData={chartData} height={YOY_CHART_LAYOUT.singleChartHeight} />;
  }

  if (profGroups.length === 0) {
    return (
      <p className="text-center text-muted-foreground text-sm py-8">
        No chart data available for the selected period.
      </p>
    );
  }

  return (
    <div className={YOY_CHART_LAYOUT.gridClass}>
      {profGroups.map(([profId, { profName, points }]) => {
        const glDate = goLiveDates?.[profId];
        const subtitle = glDate
          ? `Go-live: ${formatGoLiveDate(glDate)}`
          : undefined;

        return (
          <SingleChart
            key={profId}
            title={profName}
            subtitle={subtitle}
            chartData={buildChartData(points, granularity)}
            height={YOY_CHART_LAYOUT.gridChartHeight}
          />
        );
      })}
    </div>
  );
}
